import { AlignCenter, AlignLeft, AlignRight, X } from 'lucide-react'

function ControlLabel({ label }: { label: string }) {
  return <span className="text-xs text-[#5F6477]">{label}</span>
}

interface ToggleProps {
  label: string
  checked: boolean
  onChange: (next: boolean) => void
}

export function Toggle({ label, checked, onChange }: ToggleProps) {
  return (
    <div className="flex min-h-[28px] items-center justify-between gap-3">
      <ControlLabel label={label} />
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
          checked ? 'bg-[#6366F1]' : 'bg-[#E2E4ED]'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </button>
    </div>
  )
}

interface ColorFieldProps {
  label: string
  value?: string
  onChange: (next: string | undefined) => void
}

export function ColorField({ label, value, onChange }: ColorFieldProps) {
  return (
    <label className="flex min-h-[28px] items-center justify-between gap-2">
      <ControlLabel label={label} />
      <span className="group relative inline-block h-6 w-6 shrink-0">
        <span
          className="block h-6 w-6 rounded-md border border-[#E2E4ED]"
          style={
            value
              ? { backgroundColor: value }
              : {
                  backgroundImage:
                    'linear-gradient(45deg, #E2E4ED 25%, transparent 25%, transparent 75%, #E2E4ED 75%), linear-gradient(45deg, #E2E4ED 25%, transparent 25%, transparent 75%, #E2E4ED 75%)',
                  backgroundSize: '6px 6px',
                  backgroundPosition: '0 0, 3px 3px',
                }
          }
        />
        <input
          type="color"
          value={value ?? '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 h-6 w-6 cursor-pointer opacity-0"
        />
        {value && (
          <button
            type="button"
            aria-label="Réinitialiser la couleur"
            onClick={(e) => {
              e.preventDefault()
              onChange(undefined)
            }}
            className="absolute -right-1 -top-1 hidden h-3.5 w-3.5 items-center justify-center rounded-full border border-[#E2E4ED] bg-white text-[#8B8FA7] group-hover:flex"
          >
            <X size={9} />
          </button>
        )}
      </span>
    </label>
  )
}

interface TextFieldProps {
  label: string
  value: string
  placeholder?: string
  onChange: (next: string) => void
}

/** Rangée inline pour un champ texte libre (badge, libellé de métrique). */
export function TextField({ label, value, placeholder, onChange }: TextFieldProps) {
  return (
    <label className="flex min-h-[28px] items-center justify-between gap-2">
      <ControlLabel label={label} />
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="h-7 w-[152px] shrink-0 rounded-md border border-[#E2E4ED] bg-white px-2 text-xs text-[#1B2559] focus:border-[#6366F1] focus:outline-none"
      />
    </label>
  )
}

interface Option {
  value: string
  label: string
}

interface SelectFieldProps {
  label: string
  value: string
  options: Option[]
  onChange: (next: string) => void
}

export function SelectField({ label, value, options, onChange }: SelectFieldProps) {
  return (
    <label className="flex min-h-[28px] items-center justify-between gap-2">
      <ControlLabel label={label} />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-7 w-[152px] shrink-0 rounded-md border border-[#E2E4ED] bg-white px-2 text-xs text-[#1B2559] focus:border-[#6366F1] focus:outline-none"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  )
}

interface AlignPickerProps {
  label: string
  value: 'left' | 'center' | 'right'
  onChange: (next: 'left' | 'center' | 'right') => void
}

const ALIGN_OPTIONS: { value: 'left' | 'center' | 'right'; label: string; Icon: typeof AlignLeft }[] = [
  { value: 'left', label: 'Aligner à gauche', Icon: AlignLeft },
  { value: 'center', label: 'Centrer', Icon: AlignCenter },
  { value: 'right', label: 'Aligner à droite', Icon: AlignRight },
]

export function AlignPicker({ label, value, onChange }: AlignPickerProps) {
  return (
    <div className="flex flex-col gap-1">
      <ControlLabel label={label} />
      <div className="inline-flex gap-1">
        {ALIGN_OPTIONS.map(({ value: optValue, label: optLabel, Icon }) => {
          const active = value === optValue
          return (
            <button
              key={optValue}
              type="button"
              aria-label={optLabel}
              aria-pressed={active}
              onClick={() => onChange(optValue)}
              className={`flex h-8 w-8 items-center justify-center rounded-md border transition-colors ${
                active
                  ? 'border-[#6366F1] bg-[#F4F5FA] text-[#6366F1]'
                  : 'border-[#E2E4ED] text-[#8B8FA7] hover:bg-[#F4F5FA]'
              }`}
            >
              <Icon size={16} />
            </button>
          )
        })}
      </div>
    </div>
  )
}

interface SizeSelectProps {
  label: string
  value: string
  options: Option[]
  onChange: (next: string) => void
}

export function SizeSelect({ label, value, options, onChange }: SizeSelectProps) {
  return <SelectField label={label} value={value} options={options} onChange={onChange} />
}
